// React imports.
import React, { useMemo } from "react";

//library imports
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// App imports.
import DbSelectListByCode from "../../BaseComponents/DbSelectListByCode";
import { IGranteePortalResults } from "../../baseWorker"
import { RadioOptionsEnum } from "../../BaseComponents/RadioYesNoAll"
import ReadingIndexValues from "./ReadingIndexValues";

interface BenefitTargetingComp5Props {
  state: any;
}

function BenefitTargetingComp5({ state }: BenefitTargetingComp5Props) {
  // Initialize options with useMemo to avoid recalculation
  const options = useMemo(() => {
    const baseOptions = {
      credits: {
        enabled: false
      },
      chart: {
        type: 'column',
      },
      title: {
        text: 'FY ' + state.SelectedIndexHistory.LatestYear.Year + ' ' + state.SelectedIndexHistory.SelectedIndexString + ' Index'
      },
      xAxis: {
        labels: {
          enabled: true,
        },
        categories: [] as string[],
        crosshair: true
      },
      yAxis: {
        plotLines: [{
          color: '#FF0000',
          width: 2,
          value: 100,
          dashStyle: 'ShortDash',
        }],
        min: 0,
        max: 200,
        title: {
          text: state.SelectedIndexHistory.SelectedIndexString + ' Index'
        }
      },
      series: [
        {
          name: state.SelectedIndexHistory.SelectedIndexString + ' Index',
          data: [] as any[],
          showInLegend: false,
        }
      ],
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true
          }
        },
        column: {
          pointPadding: 0.2,
          borderWidth: 0
        }
      },
    };

    let dataArray: any[] = [];
    const results: IGranteePortalResults = state.GranteePortalResults;
    //get selected grantee data and other grantee data
    const selectedGranteeName: string = state.getGranteeName(state.SelectedGranteeId);
    const selectedGranteeData = results.reportData.filter(i => i.Grantee == selectedGranteeName);
    const otherGranteeData = results.reportData.filter(i => i.Grantee != selectedGranteeName);
    const BEN_TARG_COL_INDEX: number = 1;
    const GRANTEE_COL_INDEX: number = 0;

    //get grantee's data row and add benefit
    //tracking index and category
    if (selectedGranteeData.length > 0) {
      dataArray.push({
        y: +selectedGranteeData[0][results.columnNames[BEN_TARG_COL_INDEX]],
        color: 'grey'
      });

      if (+selectedGranteeData[0][results.columnNames[BEN_TARG_COL_INDEX]] > 200) {
        baseOptions.yAxis.max = +selectedGranteeData[0][results.columnNames[BEN_TARG_COL_INDEX]] + 30;
      }

      baseOptions.xAxis.categories.push(selectedGranteeData[0][results.columnNames[GRANTEE_COL_INDEX]]);
    }

    //iterate other rows in Grantee search results adding
    //benefit tracking index and grantee to chart
    if (otherGranteeData.length > 0) {
      otherGranteeData.forEach(row => {
        let prevHighDataPoint: number = 0;
        let dataPoint = +row[results.columnNames[BEN_TARG_COL_INDEX]];

        dataArray.push({
          y: dataPoint
        });
        baseOptions.xAxis.categories.push(row[results.columnNames[GRANTEE_COL_INDEX]]);

        //update the y axis if value is too high for 0-200 range normally set
        if (dataPoint > 200 && dataPoint > prevHighDataPoint) {
          baseOptions.yAxis.max = dataPoint + 30;
          prevHighDataPoint = dataPoint;
        }
      });
    }
    baseOptions.series[0].data = dataArray;
    
    return baseOptions;
  }, [state]);

  return (
    <div >
      <h2>{state.SelectedIndexHistory.SelectedIndexString}  Index  – Custom Comparison Results</h2>
      <br />
      <p>Below are the results from your custom comparison:</p>

      <div style={{ "width": "100%", "overflow": "auto" }}>
        <table id="tblDataReliabilityActionPlan">
          <thead>
            <tr>
              <th></th>
              <th></th>
              {state.GranteePortalResults.columnHeaders.map((row, index) => {
                if (index > 1) {
                  return (
                    <th key={index} colSpan={row[1]}>{row[0]}</th>
                  );
                }
                return null;
              })
              }
            </tr>
            <tr>
              <th>Grant Recipient</th>
              <th>{state.SelectedIndexHistory.SelectedIndexString}   Index</th>
              {state.GranteePortalResults.columnNames.map((row, index) => {
                if (index > 1) {
                  return (
                    <th key={index}>{row}</th>
                  );
                }
                return null;
              })
              }
            </tr>
          </thead>
          <tbody>
            {state.GranteePortalResults.reportData.map((row, rowIndex) => {
              const granteeColumnName = state.GranteePortalResults.columnNames[0];
              const indexColumnName = state.GranteePortalResults.columnNames[1];
              return (
                <tr key={rowIndex}>
                  <td>{row[granteeColumnName]}</td>
                  <td>{(+row[indexColumnName]).toLocaleString()}</td>
                  {Object.entries(row).map(([key, value]) => {
                    if (key.indexOf("Index") == -1 && key !== "Grant Recipient") {
                      return (
                        <td key={key}><span dangerouslySetInnerHTML={{ __html: String(value) }} /></td>
                      );
                    }
                    return null;
                  }
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <br /><br />
      <div className="container">
        <div className="row">
          <div className="col-sm-7">
            <HighchartsReact highcharts={Highcharts} options={options} />
          </div>
          <div className="col-sm-5 takeaways">
            <ReadingIndexValues history={state.SelectedIndexHistory} />
          </div>
        </div>
      </div>
      <br /><br />
      <p>If you would like to know more detailed information about the states in your comparison, please also try out a related tool called the <a href="/datawarehouse/grantee_search_portal/">Grantee Search Portal</a>. </p>
      <p>Otherwise, you can print your results, download index values for these grantees as a CSV file, conduct a new comparison, or return to home using the buttons below.</p>

    </div>

  );
}

export default BenefitTargetingComp5;
